import { minutesToMilliseconds } from 'date-fns'
import { getPriceIdMap, GetTierFunction } from '.'
import { AiUserSubscription, BillingCycle } from '../../types'
import { BaseTier, CurrencySymbols, GetPriceIdType, TrainingSpeed } from './BaseTier'
import {
  BRAZILIAN_PRICING_CUTOFF_DATE,
  BRAZILIAN_PRICING_SUNSET_CUTOFF_DATE,
  CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE,
  CREATOR_UNLIMITED_DOWNLOAD_CUTOFF_DATE
} from './constants'

export type CreatorTierType = CreatorTier | Creator75Tier | LegacyCreatorTier | CreatorBrazilTier

/**
 * NOTE: updates to this will effect LegacyCreatorTier and Creator75Tier as well
 */
class CreatorTier extends BaseTier {
  readonly subscriptionType = AiUserSubscription.CREATOR
  readonly subscriptionLevel = 3
  readonly vocalLimitMinutes = 100
  readonly downloadMsPerMonth = minutesToMilliseconds(60)
  readonly textToSpeechCharacterLimit = 2500
  readonly modelSlots = 5

  readonly inferenceSecondsPerMonth = Infinity
  readonly advancedSettingsEnabled = true
  readonly artistVoiceModelsEnabled = true
  readonly textToSpeechCharacterConverts = 30_000

  readonly trainingSpeed = TrainingSpeed.PRIORITY
  readonly maxTrainingDatasetMb = 500
  readonly maxTrainDatasetFileSizeMb = 200
  readonly maxTrainingDatasetDurationMinutes = 60
  readonly maxYoutubeTrainingVideoLengthMinutes = 20
  readonly maxMlTrainingJobsPerMonth = Infinity
  readonly youtubeConvertsPerDay = Infinity

  readonly currencySymbol: CurrencySymbols = CurrencySymbols.USD
  readonly monthlyPrice: number = 24.99
  readonly annualPrice: number = 240

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CREATOR_PRICE
  }
}

/**
 * The tier for users that created accounts after BRAZILIAN_PRICING_CUTOFF_DATE
 */
class CreatorBrazilTier extends CreatorTier {
  readonly currencySymbol = CurrencySymbols.BRL
  readonly monthlyPrice: number = 62.5
  readonly annualPrice: number = 600

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CREATOR_PRICE_BRAZIL
  }
}

/**
 * The tier for users that created accounts before CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE
 */
class Creator75Tier extends CreatorTier {
  readonly downloadMsPerMonth = minutesToMilliseconds(75)
}

/**
 * The tier for users that created accounts before CREATOR_UNLIMITED_DOWNLOAD_CUTOFF_DATE
 */
class LegacyCreatorTier extends CreatorTier {
  readonly downloadMsPerMonth = Infinity
}

const legacyCreatorTierInstance = new LegacyCreatorTier()
const creator75TierInstance = new Creator75Tier()
const creatorTierInstance = new CreatorTier()
const creatorBrazilTierInstance = new CreatorBrazilTier()

export const getUserCreatorTier: GetTierFunction<CreatorTierType> = (user) => {
  if (!user) return creatorTierInstance
  const createdAtDate = new Date(user.createdAt)

  const brazilSunsetCutoffDate = new Date(BRAZILIAN_PRICING_SUNSET_CUTOFF_DATE)
  const brazilCutoffDate = new Date(BRAZILIAN_PRICING_CUTOFF_DATE)
  if (
    createdAtDate > brazilCutoffDate &&
    createdAtDate < brazilSunsetCutoffDate &&
    user.countryCode === 'BR'
  ) {
    return creatorBrazilTierInstance
  }

  const unlimitedCutoffDate = new Date(CREATOR_UNLIMITED_DOWNLOAD_CUTOFF_DATE)
  if (createdAtDate < unlimitedCutoffDate) return legacyCreatorTierInstance

  const creator75CutoffDate = new Date(CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE)
  if (createdAtDate < creator75CutoffDate) return creator75TierInstance

  return creatorTierInstance
}

export {
  creatorTierInstance as CreatorTier,
  creator75TierInstance as Creator75Tier,
  legacyCreatorTierInstance as LegacyCreatorTier,
  creatorBrazilTierInstance as CreatorBrazilTier
}
